import { useState, type KeyboardEvent } from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import { STATE_PALETTE } from '../../utils/statePalette';

export interface AddStateColumnProps {
  /**
   * Number of states currently rendered on the board. Used to deterministically
   * pick the next swatch from STATE_PALETTE so a fresh project's columns step
   * through the palette in the same order they would on the project edit page
   * (StateListEditor.tsx:90).
   */
  stateCount: number;
  onAdd: (name: string, color: string) => void | Promise<void>;
}

/**
 * Quick task 260610-kv6 — trailing "+ Add state" cell rendered next to the
 * Kanban Board's column-header strip. Mirrors the inline-add UX from
 * StateListEditor (project edit page): collapsed → primary-coloured button
 * with PlusIcon, expanded → name input. Enter or blur commits, Esc cancels.
 * Color is auto-picked from STATE_PALETTE based on the current state count
 * so first-time adds step through the palette deterministically; users who
 * want a different colour click the swatch on the resulting column header
 * afterwards (ColumnHeaderCell recolor flow).
 *
 * Rendered ONLY when canManageStates is true — the page-level gate prevents
 * external actors and non-admin/non-PM internal users from seeing this slot
 * (parity with ColumnHeaderCell's edit affordances and StateListEditor's
 * read-only mode in the project surface).
 */
export function AddStateColumn({ stateCount, onAdd }: AddStateColumnProps) {
  const [isAdding, setIsAdding] = useState(false);

  function commit(name: string) {
    const trimmed = name.trim();
    if (trimmed) {
      const nextColor =
        STATE_PALETTE[stateCount % STATE_PALETTE.length] ?? STATE_PALETTE[0];
      void onAdd(trimmed, nextColor);
    }
    setIsAdding(false);
  }

  function handleKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      (e.currentTarget as HTMLInputElement).blur();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsAdding(false);
    }
  }

  if (isAdding) {
    return (
      <div
        className="flex items-center px-3 py-2 rounded-lg border border-dashed border-ms-border bg-ms-surface"
        data-testid="add-state-column-form"
      >
        <input
          type="text"
          autoFocus
          placeholder="New state name"
          aria-label="New state name"
          onBlur={(e) => commit(e.currentTarget.value)}
          onKeyDown={handleKey}
          className="w-full text-sm bg-transparent focus:outline-none text-ms-text placeholder:text-ms-text-muted"
        />
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setIsAdding(true)}
      className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-dashed border-ms-border bg-ms-surface text-sm font-medium text-primary-600 hover:text-primary-700 hover:bg-primary-50/30 transition-colors"
      data-testid="add-state-column-trigger"
      aria-label="Add new task state column"
    >
      <PlusIcon className="h-4 w-4" />
      Add state
    </button>
  );
}
